<template>
  <!-- 头部显示内容-->
  <div class="banner-header">
    <div class="banner-content">
      <div class="game-name">{{ gameName }}</div>
      <div class="header-actions">
        <LanguageSelector />
        <div
          v-if="languageStore.showSearch"
          class="search-btn"
          @click="goToSearch"
        >
          <img src="@/assets/search.png" alt="search" class="search-icon" />
        </div>
      </div>
    </div>
  </div>
  <!-- Banner区域 -->
  <div
    class="banner-section"
    :class="{ 'is-fixed': isBannerFixed }"
    :style="{ backgroundImage: `url(${homeBanner})` }"
  >
    <div class="banner-role" v-show="!isBannerFixed">
      <div class="banner-content">
        <div class="role-info" @click="handleBindRole">
          <Avatar
            :avatar-index="userStore.avatarIndex"
            size="small"
            class="role-avatar"
            :userId="userStore.uid"
            :avatar-frame="userStore.avatarFrame"
          />
          <span class="role-text">{{ roleDisplayText }}</span>
        </div>
        <div
          class="sign-btn"
          @click="handleSign"
          :class="{ 'is-signed': userStore.isSigned }"
        >
          <img src="@/assets/sign.png" alt="sign" class="sign-icon" />
          <span>{{
            forumStore.isSigned ? t("home.hasSigned") : t("home.signIn")
          }}</span>
        </div>
      </div>
    </div>
  </div>
  <SignSuccessModal v-model="showSignSuccessModal" />
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useUserStore, useForumStore, useToastStore } from "@/stores";
import { getGameConfig } from "@/config/gameConfig";
import { clickButton } from "@/utils/point";
import { pageType } from "@/utils/point";
import { useLanguageStore } from "@/stores/language";
// @ts-ignore
import Avatar from "@/components/common/Avatar.vue";
// @ts-ignore
import LanguageSelector from "@/components/common/LanguageSelector.vue";
// @ts-ignore
import SignSuccessModal from "@/components/modal/SignSuccessModal.vue";

interface Props {
  isBannerFixed: boolean;
}

defineProps<Props>();

const { t } = useI18n();
const router = useRouter();
const userStore = useUserStore();
const forumStore = useForumStore();
const toast = useToastStore();
const gameConfig = getGameConfig();
const languageStore = useLanguageStore();
const showSignSuccessModal = ref(false);

const homeBanner = computed(() => {
  return languageStore.getImage("homeBanner") || gameConfig.homeBanner;
});
// 游戏名称：优先使用扩展参数 real_name，无则用 gameConfig.title 兜底
const gameName = computed(
  () => languageStore.realName || gameConfig.title
);
// 角色显示文本计算属性
const roleDisplayText = computed(() => {
  if (!userStore.isLogin) {
    // 未登录，显示请先登录
    return t("home.pleaseLogin");
  } else if (userStore.bindInfo?.role_id) {
    // 已绑定角色，显示 serverName-roleName
    const bindInfo = userStore.bindInfo;
    return `${decodeURIComponent(bindInfo.server_name)}-${decodeURIComponent(
      bindInfo.role_name
    )}`;
  } else {
    // 已登录但未绑定角色，显示默认文本
    return t("home.unboundRole");
  }
});

// 搜索跳转
const goToSearch = () => {
  clickButton({
    eventType: pageType.HOME,
    eventReason: "搜索",
  });
  router.push("/search");
};

// 签到处理
const handleSign = async () => {
  if (userStore.isSigned) {
    return;
  }
  if (!userStore.checkLogin()) {
    return;
  }
  clickButton({
    eventType: pageType.HOME,
    eventReason: "签到",
  });
  const res = await userStore.sign();
  if (res?.code == 200) {
    if (languageStore.showPoint) {
      showSignSuccessModal.value = true;
    } else {
      toast.showToast(t("home.signSuccess"), "success", 1000);
    }
  } else {
    toast.showToast(t("home.signFailed"), "error", 1000);
  }
};

// 绑定角色处理
const handleBindRole = () => {
  if (!userStore.checkLogin()) {
    return;
  }
  clickButton({
    eventType: pageType.HOME,
    eventReason: "角色切换/绑定",
  });
  userStore.openBindModal();
};
</script>

<style lang="scss" scoped>
// 变量定义
$banner-height: 160px;
$banner-fixed-height: 60px;
$primary-color: #dd8632;
$text-color: #333;
$white: #fff;
$shadow-sm: 0 2px 8px rgba(104, 60, 60, 0.1);
$shadow-md: 0 2px 4px rgba(0, 0, 0, 0.4);
$border-radius-lg: 20px;
$border-radius-sm: 8px;
$spacing-base: 12px;
$spacing-sm: 8px;
$pc-content-block-half: 550px; // 与 Home.vue 一致，(800+20+280)/2，用于居中后内容列左缘
$pc-content-full-width: 1100px; // PC 端内容+侧栏总宽 800+20+280，与 Home 一致

.banner-section {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: $banner-height;
  background: {
    size: cover;
    position: center;
    repeat: no-repeat;
  }
  z-index: 10;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
  }

  &.is-fixed {
    height: $banner-fixed-height;
    z-index: 10;
  }
}

.banner-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  padding: 12px $spacing-base;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1000;

  .game-name {
    color: $white;
    font-size: 25px;
    font-weight: 600;
    text-shadow: $shadow-md;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  .search-btn {
    width: 32px;
    height: 32px;
    background: rgba($white, 0.15);
    backdrop-filter: blur(4px);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    .search-icon {
      width: 16px;
      height: 16px;
    }
  }

  &.is-fixed {
    background: rgba(0, 0, 0, 0.4);
  }
}
.banner-content {
  max-width: 800px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

@media (min-width: 1024px) {
  .banner-content {
    max-width: $pc-content-full-width;
    width: 100%;
  }
}
.banner-role {
  position: absolute;
  top: 75px;
  left: 0;
  right: 0;
  padding: 12px $spacing-base;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 2;
}

.role-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $spacing-sm;
  text-shadow: $shadow-md;
  padding: 5px 0;
  cursor: pointer;

  .role-text {
    color: $white;
    font-size: 14px;
    font-weight: 600;
  }
}

.sign-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px $spacing-base;
  background: $white;
  border-radius: $border-radius-lg;
  box-shadow: $shadow-sm;
  transition: transform 0.3s ease;
  cursor: pointer;
  &.is-signed {
    background: #f0f0f0;
    cursor: not-allowed;
    pointer-events: none;
    .sign-icon {
      filter: invert(0.5);
    }
    span {
      color: #666;
      font-weight: 600;
    }
  }
  &:active {
    transform: scale(0.98);
  }

  .sign-icon {
    width: 16px;
    height: 16px;
  }

  span {
    color: $text-color;
    font-size: 12px;
    white-space: nowrap;
    font-weight: 500;
  }
}
</style>
